import $ from 'jquery';
import h from 'hyperscript';
import { getHeight, getWidth } from 'ol/extent';
import PointerInteraction from 'ol/interaction/Pointer';
import MapBrowserEventType from 'ol/MapBrowserEventType';
import WktFormat from 'ol/format/WKT';
import GeoJSONFormat from 'ol/format/GeoJSON';
import pointOnFeature from '@turf/point-on-feature';
import { unByKey as unByKeyOl } from 'ol/Observable';

import { SWITCH_TAB_EVENT } from 'ui/tabs/tabs';
import {
  closeAllPopups,
  createPopup,
  doesPopupFitIntoTheScreen,
  openPopup,
  PLACEMENT
} from 'ui/popup/popup';

import { findAddresses } from 'cadenza/api-client/geocoding-api';
import { MapsApi } from 'cadenza/api-client/maps-api';
import {
  fakeMouseEventForPositioning,
  showLegacyDialog
} from 'cadenza/legacy-dialog/legacy-dialog';
import { context } from 'cadenza/map/context/context';
import { LayerProperty } from 'cadenza/map/layer-property';
import { MapProperty } from 'cadenza/map/map-property';
import { resolutionForScale } from 'cadenza/map/map-utils';
import { enableFeatureZoomDecorator } from 'cadenza/map/feature-info-utils';
import { logger } from 'cadenza/utils/logging';
import { isCloseToZero } from 'cadenza/utils/math';
import { mediaXsOnly } from 'cadenza/utils/media';
import { throttle } from 'cadenza/utils/throttle';
import { on } from 'cadenza/utils/event-util';
import { MIMETYPE_HTML } from 'cadenza/model/media-type';

// there is also an implicit dependency on 'ui/tabs/tabs': it is required for the featureInfo dialog
// provided by defaultFeatureInfo.xhtml (via jsf template). Which means: Even if we wouldn't have
// any explicit dependencies here, we would still need to just `import 'ui/tabs/tabs';`

import './feature-info.css';
import i18n from './interactions.properties';

const MAP_TAG_HIGHLIGHT_GEOMETRY = 'web:map:featureinfo:autohighlightgeometry';
const SCRIPTED_LAYER_ID_TAG_NAME = 'map:scriptedLayerId';

export const TEXT_HTML_INFO_FORMAT = MIMETYPE_HTML;
const MAP_TIP_INFO_FORMAT = 'application/disyMapTip';
const MAP_TIP_REQUEST_DELAY = 2000;
const ZOOM_TO_POINT_SCALE = 10000; // same as in Desktop (Display.calculateTargetWorldBox)

export class FeatureInfo extends PointerInteraction {

  #dialog;

  constructor (map, {
    requestOnHover = true,
    requestOnClick = true,
    requestDelay = 0,
    maintainPosition = false,
    reverseSelectedLayers = true,
    maxFeatures = 10,
    featureInfoUrl
  } = {}) {
    super({ handleEvent: event => this._handleEvent(event) });
    this.setActive(false);
    this._map = map;
    this._mapApi = new MapsApi({ featureInfoUrl });
    this._requestOnHover = requestOnHover;
    this._requestOnClick = requestOnClick;
    this._requestDelay = this._requestOnHover ? MAP_TIP_REQUEST_DELAY : requestDelay;
    this._maintainPosition = maintainPosition;
    this._reverseSelectedLayers = reverseSelectedLayers;
    this._maxFeatures = maxFeatures;
    this._onMouseOutHandler = () => this._clearScheduledRequest();

    this.on('change:active', event => {
      if (!event.oldValue) {
        this._onActivation();
      } else {
        this._onDeactivation();
      }
    });

    this.adjustMaxHeight = throttle(() => {
      this.#dialog?.adjustMaxHeight();
    }, 200);
  }

  _clearScheduledRequest () {
    clearTimeout(this._isRequestScheduled);
    this._isRequestScheduled = null;
  }

  _onActivation () {
    if (this._requestOnHover) {
      this._map.getViewport().addEventListener('mouseout', this._onMouseOutHandler);
    }

    this._hasBeenDeactivated = false;
    $(window).on('resize', this.adjustMaxHeight);
  }

  _onDeactivation () {
    context(this._map).resetHighlighting();
    if (this._requestOnHover) {
      this._map.getViewport().removeEventListener('mouseout', this._onMouseOutHandler);
    }

    this._hasBeenDeactivated = true;
    $(window).off('resize', this.adjustMaxHeight);
  }

  _handleEvent (mapBrowserEvent) {
    if (mapBrowserEvent.type === MapBrowserEventType.POINTERMOVE && this._requestOnHover) {
      this._hideCurrentFeatureInfo();
      if (legendHasRunningTasks()) {
        this._clearScheduledRequest();
      } else {
        this._makeWmsRequestWithDelay(mapBrowserEvent, this._requestDelay, MAP_TIP_INFO_FORMAT);
      }
      return false;
    }
    if (mapBrowserEvent.type === MapBrowserEventType.CLICK && this._requestOnClick) {
      this._makeWmsRequestWithDelay(mapBrowserEvent, 0, TEXT_HTML_INFO_FORMAT);
      return false;
    }
    return super.handleEvent(mapBrowserEvent);
  }

  _makeWmsRequestWithDelay (originalEvent, delay, infoFormat) {
    clearTimeout(this._isRequestScheduled);
    const pageCoordinates = [ originalEvent.originalEvent.clientX, originalEvent.originalEvent.clientY ];
    this._isRequestScheduled = setTimeout(() => this.makeRequest(originalEvent.pixel, originalEvent.coordinate, pageCoordinates, infoFormat), delay);
  }

  makeRequest (pixel, coordinate, pageCoordinates, infoFormat) {
    const [ x, y ] = pageCoordinates;
    if (infoFormat !== TEXT_HTML_INFO_FORMAT && !doesPopupFitIntoTheScreen(x, y)) {
      return;
    }
    const featureInfoLayers = getVisibleLayersWithFeatureInfo(this._map);
    if (featureInfoLayers.length) {
      const selectedLayer = this._map.get(MapProperty.SELECTED_LAYER);
      const firstLayer = featureInfoLayers[0];
      const featureInfoArgs = {
        mapId: this._map.get(MapProperty.ID),
        layersIDs: this._reverseSelectedLayers ? featureInfoLayers.map(getID).reverse() : featureInfoLayers.map(getID),
        selectedLayerID: selectedLayer ? getID(selectedLayer) : '',
        scriptedLayersIDs: getScriptedLayersIDs(this._map),
        projection: this._map.getView().getProjection().getCode(),
        mapSize: this._map.getSize(),
        mapExtent: this._map.getView().calculateFullExtent(),
        clickPosition: pixel,
        version: firstLayer.get(LayerProperty.FEATURE_INFO_VERSION),
        maxFeatures: this._maxFeatures,
        format: getLayerParam(firstLayer, 'FORMAT'),
        infoFormat: getLayerParam(firstLayer, 'INFO_FORMAT') || infoFormat,
        styles: getStyleNames(featureInfoLayers)
      };

      context(this._map).loading = true;
      this._mapApi.getFeatureInfo(featureInfoArgs)
        .then(res => this._displayOverlayResult(coordinate, pageCoordinates, res, infoFormat))
        .catch(error => {
          logger.error('Cannot obtain feature information', error);
        })
        .finally(() => { context(this._map).loading = false; });
    }
  }

  _displayOverlayResult (coordinate, pageCoordinates, response, infoFormat) {
    if (response && !this._hasBeenDeactivated) {
      const document = new DOMParser().parseFromString(response, MIMETYPE_HTML);
      const featureInfo = window.document.createDocumentFragment();
      Array.from(document.body.childNodes).forEach(childNode => featureInfo.appendChild(childNode));
      if (infoFormat === TEXT_HTML_INFO_FORMAT) {
        enableFeatureZoomDecorator(featureInfo, (decorator) =>
          this._zoomToFeature(decorator)
        );
        if (mediaXsOnly.matches) {
          this._closeAllExceptFirstCollapsiblePanels(featureInfo);
        } else {
          this._enableFeatureHighlight(featureInfo);
          this._firstDialogPosition = this._firstDialogPosition || pageCoordinates;
        }
        this._showFeatureInfoDialog(this._maintainPosition ? this._firstDialogPosition : pageCoordinates, featureInfo);
        this.adjustMaxHeight();
      } else if (!this._maintainPosition) {
        const popup = createPopup({ styleClass: [ 'map-tip-popup', 'ellipsis' ] });
        popup.append(featureInfo);
        openPopup(new DOMRect(pageCoordinates[0], pageCoordinates[1], 0, 0), popup, {
          arrow: true,
          placement: PLACEMENT.TOP
        });
      }
    }
  }

  _retrieveAddress (geometryText, _map) {
    const srid = _map.getView().getProjection().getCode();
    const interiorPoint = getInteriorPoint(geometryText, srid);
    return findAddresses(interiorPoint, srid)
      .catch(() => ({ formattedAddressLines: [ i18n('addressInfo.addressRequestFailed') ] }));
  }

  _zoomToFeature (decorator) {
    const featureExtent = JSON.parse(decorator.previousElementSibling.textContent);
    const isPoint = isCloseToZero(getWidth(featureExtent)) && isCloseToZero(getHeight(featureExtent));
    const options = {};
    if (isPoint) {
      options.minResolution = resolutionForScale(this._map.getView(), ZOOM_TO_POINT_SCALE);
    }
    this._map.getView().fit(featureExtent, options);
    return false;
  }

  _enableFeatureHighlight (featureInfo) {
    const tags = this._map.get(MapProperty.TAGS) || [];
    const highlightTag = tags.find(tag => tag.name === MAP_TAG_HIGHLIGHT_GEOMETRY);
    if (highlightTag && highlightTag.value !== 'true') {
      return;
    }

    $('.featureHighlightDecorator', featureInfo).each((i, decorator) => {
      $(decorator).nextUntil('.featureHighlightDecorator-end')
        .on('mouseover', () => {
          const data = decorator.previousElementSibling.textContent;
          context(this._map).highlightFeatures([ new WktFormat().readFeature(data) ]);
        })
        .on('mouseleave', () => {
          context(this._map).resetHighlighting();
        });
    });
  }

  _closeAllExceptFirstCollapsiblePanels (featureInfo) {
    const panels = featureInfo.querySelectorAll('.d-panel-section');
    panels.forEach((panel, index) => { panel.open = (index === 0); });
  }

  _hideCurrentFeatureInfo () {
    closeAllPopups(popup => popup.classList.contains('map-tip-popup'));
  }

  _showFeatureInfoDialog (pageCoordinates, featureInfo) {
    const positioning = {
      my: 'left+10 top+10',
      at: 'right bottom',
      within: this._map.getViewport(),
      of: fakeMouseEventForPositioning(pageCoordinates[0], pageCoordinates[1])
    };

    const dialogOptions = Object.assign({
      content: featureInfo,
      title: i18n('objectInformation.title'),
      containerClass: 'feature-info-dialog',
      closeable: true,
      fullScreenOnSmallDevices: true,
      resizeable: true,
      positioning
    });
    const size = this._calcSize(featureInfo);
    if (size) {
      if (size[0] === 'auto') {
        dialogOptions.autoWidth = true;
      } else {
        dialogOptions.width = size[0];
      }

      if (size[1] === 'auto') {
        dialogOptions.autoHeight = true;
      } else {
        dialogOptions.height = size[1];
      }
    } else {
      dialogOptions.autoHeight = true;
      dialogOptions.autoWidth = true;
    }

    this.#dialog = showLegacyDialog(dialogOptions);
    this.#dialog.addEventListener('close', () => document.body.removeChild(this.#dialog));

    // Close dialog when all layers we got feature infos from have been removed.
    const layers = this._map.getLayers();
    const that = this;
    const eventKey = layers.on('remove', (event) => {
      const removedLayerId = event.element.get(LayerProperty.ID);
      that._getAllFeatureInfoLayerIdElementsFromDialog()
        // Filter to those feature infos that were created from the layer that have been removed
        .filter(element => element.textContent === removedLayerId)
        // Collect all feature info elements corresponding to these feature infos.
        // It assumes that the customer might insert multiple elements instead of just one per
        // feature info since they can use their own template.
        .flatMap(element => {
          const elementsToRemove = [ element ];
          let featureInfoElement = element.nextElementSibling;
          while (featureInfoElement && !featureInfoElement.matches('.featureInfo-layerId, .noDataAvailable')) {
            elementsToRemove.push(featureInfoElement);
            featureInfoElement = featureInfoElement.nextElementSibling;
          }
          return elementsToRemove;
        })
        // Delete them
        .forEach(elementToRemove => elementToRemove.remove());

      // No feature info left? Then close the dialog!
      if (!that._getAllFeatureInfoLayerIdElementsFromDialog().length) {
        this.#dialog.close();
        unByKeyOl(eventKey);
      }
    });

    // Remove listener to the layers collection as it otherwise would stay after closing the dialog
    this.#dialog.addEventListener('close', () => unByKeyOl(eventKey));

    const retrieveAddress = this._retrieveAddress;
    on(this.#dialog, SWITCH_TAB_EVENT, event => {
      const tabs = event.target;
      const selectedTab = event.detail.tab;
      const selectedTabPanel = tabs.getTabPanel(selectedTab);
      if (selectedTabPanel.classList.contains('feature-info--address-tab-value') && !selectedTabPanel.requestedAddress) {
        const featureGeom = tabs.closest('.d-panel-section').parentNode.textContent;
        retrieveAddress(featureGeom, this._map)
          .then(addr => {
            if (addr && addr.formattedAddressLines) {
              selectedTabPanel.textContent = addr.formattedAddressLines.join('\n');
              // only happens once, because the featureInfo only requests the address once
              selectedTabPanel.appendChild(h('div.address-is-computed-warning', i18n('addressInfo.addressIsComputed')));
            } else {
              selectedTabPanel.textContent = i18n('addressInfo.noAddressFound'); // i.e. 204 from reverse geocoding endpoint.
            }
          })
          .catch(() => logger.warn('could not set address for address tab', selectedTab));
        selectedTabPanel.requestedAddress = true;
      }
    }, { delegate: '.d-tabs' });
  }

  _getAllFeatureInfoLayerIdElementsFromDialog () {
    return [ ...this.#dialog.querySelectorAll('.featureInfo-selectedLayerId') ];
  }

  _calcSize (featureInfo) {
    const size = featureInfo.querySelector('.featureInfoSize');
    if (!size) {
      return null;
    }
    const [ featureInfoWidth, featureInfoHeight ] = size ? JSON.parse(size.textContent) : [];
    const $content = $('.d-page--content');
    const contentHeight = $content.height();
    const contentWidth = $content.width();

    let width = this._parseValue(featureInfoWidth);
    let height = this._parseValue(featureInfoHeight);

    if (height !== 'auto' && height > contentHeight) {
      height = contentHeight - 20;
    }

    if (width !== 'auto' && width > contentWidth) {
      width = contentWidth - 20;
    }

    return [ width, height ];
  }

  _parseValue (n) {
    if (n === 'auto') {
      return n;
    } else {
      const num = parseInt(n);
      return isNaN(num) ? 'auto' : num; // Number.isNaN is not supported in IE
    }
  }

  get resettable () {
    return true;
  }

}

function getVisibleLayersWithFeatureInfo (map) {
  return context(map).layers.filter(layer => layer.get(LayerProperty.VISIBLE_IN_LAYERLIST) && layer.get(LayerProperty.HAS_FEATURE_INFO));
}

function getScriptedLayersIDs (map) {
  return map.get(MapProperty.TAGS)
    .filter(tag => tag.name === SCRIPTED_LAYER_ID_TAG_NAME)
    .map(tag => tag.value);
}

function getID (layer) {
  return layer.get(LayerProperty.ID);
}

function getStyleNames (layers) {
  return layers
    .map(layer => getLayerSourceParam(layer, 'STYLES'))
    .filter(styles => !!styles);
}

function getLayerSourceParam (layer, paramName) {
  const layerSource = layer.getSource();
  if (layerSource && layerSource.getParams) {
    return layerSource.getParams()[paramName];
  }
}

function getLayerParam (layer, paramName) {
  const layerSource = layer.getSource();
  if (layerSource && layerSource.getParams) {
    return layerSource.getParams()[paramName];
  }
}

function legendHasRunningTasks () {
  const legend = document.querySelector('d-map-legend');
  return legend && legend.legendTasksRunning();
}

function getInteriorPoint (geometryText, srid) {
  const feature = new WktFormat().readFeature(geometryText);
  const geoJSONFormat = new GeoJSONFormat({ featureProjection: srid });
  const point = pointOnFeature(geoJSONFormat.writeFeatureObject(feature));
  const interiorPoint = geoJSONFormat.readFeature(point).getGeometry();
  return interiorPoint;
}
